import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { ChargePointTariffUpdateController } from '@app/feature/charge-point/charge-point-tariff-update/charge-point-tariff-update.controller';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';

import * as css from '../../../charge-point-details.module.scss';

export const TariffCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tariff } = useChargePointContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canUpdateTariff = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.SET_TARIFF,
      }),
    [userPermissions],
  );

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Card gap={GapEnum.M}>
        <Stack alignItems="center" justifyContent="space-between">
          <AccentTitle
            as={HeadingEnum.H2}
            icon="currency-euro-circle"
            text={t(
              'delmonicos-shared-sdk.outputs-gateway-charge-point.tariff',
            )}
            variant={AccentVariantEnum.Secondary}
          />
          {tariff ? (
            <Link
              className={css.tariffName}
              onClick={() =>
                navigate(
                  generatePath(APP_PATH.TARIFF.DETAILS, { id: tariff.id }),
                )
              }>
              <Text
                className={css.linkUnderlined}
                fontSize={TextFontSize.Medium}>
                {tariff.name}
              </Text>
            </Link>
          ) : (
            <Text className={css.tariffName}>-</Text>
          )}
          {canUpdateTariff && (
            <ButtonIcon
              onClick={() => setActionUpdateIsDialogOpen(true)}
              icon="edit-05"
            />
          )}
        </Stack>
      </Card>
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}
        confirmClose
        size={DialogSize.MEDIUM}>
        <Dialog.Header
          icon="map-01"
          onClose={() => setActionUpdateIsDialogOpen(false)}
          title={t('$.charge-point-tariff-update.title')}
        />
        <ChargePointTariffUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </>
  );
};
